import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { CHANNEL_META, TYPE_META } from "@/lib/format";
import { GOAL_OPTIONS } from "@/lib/onboardingConfig";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const inputCls =
  "w-full rounded-md border border-brand-line bg-brand-panel px-3 py-2 text-sm text-brand-ink outline-none transition-all duration-150 focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20";
const selectCls = inputCls + " appearance-none";

function Field({ label, children, error, hint }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-semibold text-brand-ink">{label}</label>
      {children}
      {hint && !error && <p className="mt-1 text-xs text-brand-muted">{hint}</p>}
      {error && <p className="mt-1 text-xs font-medium text-brand-neg">{error}</p>}
    </div>
  );
}

const urlOk = (v) => !v || /^https?:\/\/.+\..+/.test(v.trim());

export default function ProspectForm({ open, onOpenChange, onCreated }) {
  const empty = {
    name: "", entity: "persona", company: "", role_title: "", channel: "instagram",
    profile_url: "", website: "", location: "", bio_public: "", notes: "",
    opportunity_type: "cliente", avatar: "", objective: "", related_offer: "",
  };
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [avatars, setAvatars] = useState([]);
  const [goals, setGoals] = useState([]);

  useEffect(() => {
    if (open) {
      api.get("/business-brain").then((r) => {
        setAvatars(r.data?.avatars_list || []);
        setGoals(r.data?.goals_list || []);
      }).catch(() => { setAvatars([]); setGoals([]); });
    }
  }, [open]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (errors[k]) setErrors((e) => ({ ...e, [k]: "" })); };

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "El nombre es obligatorio.";
    const hasEnough = form.profile_url.trim() || form.bio_public.trim() || form.website.trim() || form.notes.trim();
    if (!hasEnough) errs.bio_public = "Añade al menos una URL, web, biografía pegada o notas para poder analizarlo.";
    if (!urlOk(form.profile_url)) errs.profile_url = "URL no válida (empieza por http).";
    if (!urlOk(form.website)) errs.website = "URL no válida (empieza por http).";
    setErrors(errs);
    if (Object.keys(errs).length || submitting) return;

    const source = form.profile_url.trim() ? "url_publica" : (form.bio_public.trim() ? "info_copiada" : "manual");
    setSubmitting(true);
    try {
      const res = await api.post("/prospects", {
        ...form,
        status: "descubierto",
        analysis_status: "borrador",
        fit_score: 0,
        source,
      });
      toast.success(`${res.data.name} guardado como borrador. Analízalo desde su ficha.`);
      onCreated(res.data);
      setForm(empty);
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="prospect-form-dialog">
        <DialogHeader>
          <DialogTitle>Nuevo contacto</DialogTitle>
          <DialogDescription>Guárdalo como borrador. El análisis con IA se hace después, desde su ficha.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Nombre" error={errors.name}>
              <input data-testid="prospect-name-input" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Nombre o del proyecto" />
            </Field>
            <Field label="Persona o empresa">
              <select data-testid="prospect-entity-select" className={selectCls} value={form.entity} onChange={(e) => set("entity", e.target.value)}>
                <option value="persona">Persona</option>
                <option value="empresa">Empresa</option>
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Empresa o proyecto">
              <input data-testid="prospect-company-input" className={inputCls} value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Su negocio" />
            </Field>
            <Field label="Cargo o función">
              <input className={inputCls} value={form.role_title} onChange={(e) => set("role_title", e.target.value)} placeholder="Su rol" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Red social">
              <select data-testid="prospect-channel-select" className={selectCls} value={form.channel} onChange={(e) => set("channel", e.target.value)}>
                {Object.entries(CHANNEL_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Field>
            <Field label="Tipo de oportunidad">
              <select className={selectCls} value={form.opportunity_type} onChange={(e) => set("opportunity_type", e.target.value)}>
                {Object.entries(TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Usuario o URL pública del perfil" error={errors.profile_url} hint="Público. No accedemos a cuentas privadas ni pedimos iniciar sesión.">
            <input data-testid="prospect-url-input" className={inputCls} value={form.profile_url} onChange={(e) => set("profile_url", e.target.value)} placeholder="https://instagram.com/usuario" />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Sitio web (opcional)" error={errors.website}>
              <input className={inputCls} value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://su-web.com" />
            </Field>
            <Field label="Ubicación (opcional)">
              <input className={inputCls} value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Ciudad o país" />
            </Field>
          </div>
          <Field label="Biografía o información pública (pegada)" error={errors.bio_public} hint="Copia aquí su bio o descripción pública. CaptAcción analiza solo esto y lo que aportes; no busca dentro de las redes.">
            <textarea data-testid="prospect-bio-input" className={inputCls} rows={3} value={form.bio_public} onChange={(e) => set("bio_public", e.target.value)} placeholder="Pega aquí su biografía o texto público." />
          </Field>
          <Field label="Tus notas">
            <textarea className={inputCls} rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Lo que sepas de este contacto." />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Avatar relacionado">
              <select data-testid="prospect-avatar-select" className={selectCls} value={form.avatar} onChange={(e) => set("avatar", e.target.value)}>
                <option value="">Sin asignar</option>
                {avatars.map((a) => <option key={a.id} value={a.name}>{a.name}</option>)}
              </select>
            </Field>
            <Field label="Objetivo relacionado">
              <select className={selectCls} value={form.objective} onChange={(e) => set("objective", e.target.value)}>
                <option value="">Sin asignar</option>
                {goals.map((g) => <option key={g} value={g}>{(GOAL_OPTIONS.find((o) => o.k === g) || {}).label || g}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Producto o servicio relacionado">
            <input className={inputCls} value={form.related_offer} onChange={(e) => set("related_offer", e.target.value)} placeholder="A qué oferta lo asocias" />
          </Field>
          <DialogFooter>
            <button type="button" onClick={() => onOpenChange(false)} className="rounded-md border border-brand-line bg-brand-panel px-4 py-2 text-sm font-semibold text-brand-ink transition-all duration-150 hover:bg-brand-ink/[0.04]">
              Cancelar
            </button>
            <button type="submit" disabled={submitting} data-testid="prospect-submit-button" className="rounded-md bg-brand-accent px-4 py-2 text-sm font-bold text-brand-canvas transition-all duration-150 hover:brightness-110 disabled:opacity-60">
              {submitting ? "Guardando…" : "Guardar borrador"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
